import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, alpha, Box, CircularProgress, Container, IconButton, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessProduct } from '../../../../MicrospecDynamicUI/Models/Business/BusinessProduct';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { PRODUCTS_COLLECTION } from '../../../constants/collectionPaths';
import UpsertProduct from './UpsertProduct';

const UpdateProduct: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { translate } = localizationProvider;
  const { productId } = useParams<{ productId: string }>();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const db = cloudProvider.firestore?.instance;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [product, setProduct] = useState<BusinessProduct | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!db || !productId) { setError(translate('product.missingParameters')); setLoading(false); return; }
      try {
        const data = await getDocById(null, null, db, PRODUCTS_COLLECTION, productId, false);
        if (data) setProduct(data as BusinessProduct);
        else setError(translate('product.productNotFound'));
      } catch (err: any) {
        setError(err.message || translate('product.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [db, productId, translate]);

  const handleSave = () => { window.location.href = '/manager/products/manage'; };
  const handleCancel = () => navigate('/manager/products/manage');

  if (loading) return <Container maxWidth="lg" sx={{ py: 4 }}><Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box></Container>;

  if (error) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleCancel} sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1), '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) } }}><ArrowBackIcon /></IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{translate('product.editTitle')}</Typography>
      </Box>
      <Alert severity="error">{error}</Alert>
    </Container>
  );

  if (!product) return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="warning">{translate('product.productNotFound')}</Alert></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleCancel} sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1), '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) } }}><ArrowBackIcon /></IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900] }}>
            {translate('product.editTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">{translate('product.updateDescription')}</Typography>
        </Box>
      </Box>
      <UpsertProduct productId={productId} onSave={handleSave} onCancel={handleCancel} />
    </Container>
  );
};

export default UpdateProduct;
